import React, { memo, useMemo } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { TransactionTypes } from 'enums/approvalManagement/approvalManagement';
import { TransactionDocument } from 'typings/accounting/transaction';
import { TransactionSourceTypes } from 'enums/accounting/transfers';

// components
import DocumentsValue from '../../DocumentsValue';
import { DescriptionSection, DescriptionItem } from '@ui';
import WireTransferTemplateInfo, {
  WireTemplate,
} from '../../WireTransferTemplateInfo';

export type WireTransactionRecipientData = {
  type: TransactionSourceTypes;
  amount: string;
  postingDate: string;
  valueDate: string;
  relatedDocuments: TransactionDocument[];
  template: WireTemplate | null;
  transactionNumber: string;
  repairedData: {
    accountName: string;
    accountNumber: string;
  } | null;
  orderingCustomer: {
    accountName: string;
    accountNumber: string;
    address: string;
    reference: string;
  };
};

interface IProps {
  journalEntryId: number | null;
  transaction: WireTransactionRecipientData;
  onUpload?: () => void;
}

const Recipient = memo(({ transaction, journalEntryId, onUpload }: IProps) => {
  const { t } = useTranslation(['accounts', 'common']);

  const descriptionData = useMemo<DescriptionItem[]>(() => {
    let result: DescriptionItem[] = [];
    const {
      type,
      amount,
      template,
      valueDate,
      postingDate,
      relatedDocuments,
      orderingCustomer,
      repairedData,
      transactionNumber,
    } = transaction;

    result = [
      {
        label: t(
          'transaction_details_modal.transaction_details.internal.transaction_number',
        ),
        description: transactionNumber,
      },
      {
        label: t('transaction_details_modal.transaction_details.internal.type'),
        description: t(`types.${TransactionTypes[type]}`),
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.internal.ordering_customer',
        ),
        description: (
          <StyledInnerDescriptionSection
            size="small"
            bordered={false}
            background="none"
            data={[
              {
                label: t(
                  'transaction_details_modal.transaction_details.internal.account_name',
                ),
                description: orderingCustomer.accountName,
              },

              {
                label: t(
                  'transaction_details_modal.transaction_details.internal.account_number',
                ),
                description: orderingCustomer.accountNumber,
              },

              {
                label: t(
                  'transaction_details_modal.transaction_details.internal.address',
                ),
                description: orderingCustomer.address,
              },

              {
                label: t(
                  'transaction_details_modal.transaction_details.internal.from_account_reference',
                ),
                description: orderingCustomer.reference,
              },
            ]}
          />
        ),
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.international.beneficiary',
        ),
        description: template ? (
          <WireTransferTemplateInfo template={template} />
        ) : null,
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.international.repaired',
        ),
        description: !repairedData ? (
          t('common:no')
        ) : (
          <StyledInnerDescriptionSection
            size="small"
            bordered={false}
            background="none"
            data={[
              {
                label: t(
                  'transaction_details_modal.transaction_details.internal.account_name',
                ),
                description: repairedData.accountName,
              },

              {
                label: t(
                  'transaction_details_modal.transaction_details.internal.account_number',
                ),
                description: repairedData.accountNumber,
              },
            ]}
          />
        ),
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.internal.amount',
        ),
        description: amount,
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.internal.posting_date',
        ),
        description: postingDate,
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.internal.value_date',
        ),
        description: valueDate,
      },

      {
        label: t(
          'transaction_details_modal.transaction_details.internal.documents',
        ),
        description: (
          <DocumentsValue
            transactionDocuments={relatedDocuments}
            journalEntryId={journalEntryId}
            onUpload={onUpload}
          />
        ),
      },
    ];

    return result;
  }, [t, transaction, journalEntryId, onUpload]);

  return <DescriptionSection data={descriptionData} />;
});

const StyledInnerDescriptionSection = styled(DescriptionSection)`
  .ant-descriptions-item-label {
    width: 250px !important;
    max-width: 250px !important;
  }
`;

export default Recipient;
